<template>
  <div class="submission-list-page">
    <!-- 标题区域 -->
    <div class="page-header">
      <div class="header-title">
        <i class="el-icon-bot"></i>
        <h2>AI助手</h2>
      </div>
      <p class="header-desc">智能文档生成工具，助力您的内容创作</p>
    </div>

    <!-- 头部筛选区域 -->
    <div class="filter-header">
      <div class="filter-left">
        <el-tabs v-model="activeTab" class="filter-tabs">
          <el-tab-pane label="全部" name="all"></el-tab-pane>
          <el-tab-pane v-if="aiConfig.science !== false" label="科普征文" name="01"></el-tab-pane>
          <el-tab-pane v-if="aiConfig.literature !== false" label="文献解读" name="02"></el-tab-pane>
          <el-tab-pane v-if="aiConfig.caseAnalysis !== false" label="案例解读" name="03"></el-tab-pane>
        </el-tabs>
      </div>
      <div class="filter-right">
        <el-select v-model="statusFilter" placeholder="状态筛选" class="status-filter">
          <el-option label="全部" value=""/>
          <el-option label="生成中" value="waiting"/>
          <el-option label="已生成" value="succeeded"/>
          <el-option label="失败" value="failed"/>
        </el-select>
        <el-button
            type="text"
            icon="el-icon-refresh"
            @click="onRefresh"
            class="refresh-btn"
        >
          刷新
        </el-button>
        <el-button
            v-if="hasEnabledDocType"
            type="primary"
            icon="el-icon-plus"
            @click="openGenerateModal"
            class="add-btn"
        >
          新增任务
        </el-button>
      </div>
    </div>

    <!-- 列表 -->
    <div class="list-wrapper">
      <div
          v-for="item in filteredList"
          :key="item.id"
          class="task-card"
          :class="{ 'is-running': item.genStatus === 'waiting' || item.genStatus === 'running' || item.genStatus === '1', 'is-failed': item.genStatus === 'failed' || item.genStatus === '3' }"
      >
        <!-- 卡片头部 -->
        <div class="card-header">
          <div class="doc-type-badge" :class="'type-' + item.genType">
            {{ getDocTypeLabel(item.genType) }}
          </div>
          <span class="status-pill" :class="getStatusClass(item.genStatus)">
            <span class="status-dot"></span>
            {{ getStatusText(item.genStatus) }}
          </span>
        </div>

        <!-- 卡片内容 -->
        <div class="card-body">
          <h3 class="task-title">{{ item.title || item.keywords || '未命名稿件' }}</h3>
          <p class="task-preview" v-if="item.content">
            {{ stripHtmlAndTruncate(item.content, 120) }}
          </p>
        </div>

        <!-- 卡片底部 -->
        <div class="card-footer">
          <div class="time-info">
            <span class="time-item">
              <i class="el-icon-clock"></i>
              {{ formatDate(item.createTime) }}
            </span>
            <span class="time-divider">|</span>
            <span class="time-item" v-if="item.updateTime">
              <i class="el-icon-check-circle"></i>
              {{ formatDate(item.updateTime) }}
            </span>
          </div>
          <div class="action-buttons">
            <el-button
                v-if="(item.genStatus === 'succeeded' || item.genStatus === '2') && item.content"
                type="text"
                size="mini"
                icon="el-icon-edit"
                @click.stop="editDocument(item)"
                class="action-btn edit-btn"
            >
              编辑
            </el-button>
            <el-button
                v-if="(item.genStatus === 'succeeded' || item.genStatus === '2') && item.resFile"
                type="text"
                size="mini"
                icon="el-icon-download"
                @click.stop="downloadFile(item.resFile)"
                class="action-btn download-btn"
            >
              下载
            </el-button>
            <el-button
                v-if="item.genStatus === 'succeeded' || item.genStatus === '2'"
                type="primary"
                size="mini"
                @click.stop="goAddDocument(item)"
                class="action-btn publish-btn"
            >
              去发布
            </el-button>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <el-empty v-if="filteredList.length === 0" description="暂无记录"/>

      <!-- 无限加载 -->
      <div v-if="filteredList.length > 0" class="load-more">
        <el-spin v-if="loading" tip="加载中..."/>
        <span v-else-if="finished" class="finished-text">没有更多了</span>
      </div>
    </div>

    <!-- 编辑器弹窗 -->
    <el-dialog
        :visible.sync="showEditor"
        width="100%"
        fullscreen
        append-to-body
        title="内容编辑"
        :show-close="false"
    >
      <div class="editor-page">
        <div class="editor-action-bar">
          <el-button size="mini" class="editor-back-btn" @click="showEditor = false">
            <i class="el-icon-arrow-left"></i> 返回
          </el-button>
          <span class="editor-word-count">{{ wordCount }} 字</span>
          <el-button type="primary" size="mini" class="editor-save-btn" @click="onSave">保存修改</el-button>
        </div>

        <QuillEditor v-model="content" height="80vh" @on-word-count-change="wordCountChange"/>
      </div>
    </el-dialog>

    <!-- AI生成任务弹窗 -->
    <el-dialog
        title="AI文档生成"
        :visible.sync="showGenerateModal"
        width="720px"
        top="3vh"
        append-to-body
        class="generate-task-dialog"
        @close="showGenerateModal = false"
    >
      <div class="generate-modal-content">
        <GenerateModal @gen-success="handleGenSuccess" @close="handleGenClose"/>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {Message, MessageBox} from 'element-ui'
import {generateList, updateGenerateArticle, getAIConfig} from '@/api/aiassistant'
import GenerateModal from './GenerateModal.vue'
import QuillEditor from '@/components/QuillEditor.vue'

export default {
  name: 'aiassistant',
  components: {
    GenerateModal,
    QuillEditor
  },
  data() {
    return {
      showId: '',
      showEditor: false,
      content: '',
      taskId: '',
      showGenerateModal: false,
      list: [],
      loading: false,
      finished: false,
      refreshing: false,
      wordCount: '',
      activeTab: 'all',
      statusFilter: '',
      aiConfig: {
        science: true,
        literature: true,
        caseAnalysis: true
      }
    }
  },
  computed: {
    filteredList() {
      return this.list.filter(item => {
        // 类型筛选
        if (this.activeTab !== 'all' && item.genType !== this.activeTab) {
          return false
        }
        // 状态筛选
        if (this.statusFilter && item.genStatus !== this.statusFilter) {
          return false
        }
        return true
      })
    },
    hasEnabledDocType() {
      return this.aiConfig.science !== false ||
             this.aiConfig.literature !== false ||
             this.aiConfig.caseAnalysis !== false
    }
  },
  watch: {
    activeTab() {
      // 切换标签时自动刷新
    },
    statusFilter() {
      // 切换状态筛选时自动刷新
    }
  },
  created() {
    const headToken = localStorage.getItem('headToken');
    if (headToken) {
      this.getGenerateList()
    }
    this.loadAIConfig()
  },
  methods: {
    goAddDocument(item) {
      // 先检查是否有项目ID
      const projectId = localStorage.getItem('projectId')
      if (!projectId) {
        Message.error('请先选择项目')
        return
      }

      // 保存 genId 用于投稿
      localStorage.setItem('genId', item.genId)

      // 触发父组件关闭AI助手并打开投稿弹窗，直接传递完整的item数据
      this.$emit('go-publish', item)
    },

    downloadFile(fileUrl) {
      if (!fileUrl) {
        Message.error('文件不存在')
        return
      }
      const link = document.createElement('a')
      link.href = fileUrl.replace(/`/g, '') // 移除可能的反引号
      link.target = '_blank'
      link.download = ''
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    onSave() {
      MessageBox.confirm(
          '保存后将生成新的文档下载地址，原文档地址将失效。确定要保存吗？',
          '保存确认',
          {
            confirmButtonText: '确定保存',
            cancelButtonText: '取消',
            type: 'warning',
            closeOnClickModal: false
          }
      ).then(() => {
        updateGenerateArticle({
          genId: this.showId,
          content: this.content
        }).then(res => {
          if (res.code === 200) {
            this.getGenerateList()
            Message.success('保存成功，已生成新的文档下载地址')
            this.showEditor = false
          } else {
            Message.error(res.msg || '保存失败')
            this.showEditor = false
          }
        }).catch(() => {
          Message.error('保存失败')
        })
      }).catch(() => {
        Message.info('已取消保存')
      })
    },

    editDocument(item) {
      if (!item || !item.content) {
        Message.error('内容不存在')
        return
      }
      this.showId = item.genId
      this.content = item.content
      this.showEditor = true
    },

    wordCountChange(val) {
      this.wordCount = val
    },

    getGenerateList() {
      generateList().then(res => {
        if (res.code === 200) {
          this.list = res.data || this.getMockData()
          this.finished = true
        } else {
          // 如果接口失败，使用模拟数据
          this.list = this.getMockData()
          this.finished = true
        }
      }).catch(() => {
        // 网络异常时使用模拟数据
        this.list = this.getMockData()
        this.finished = true
      })
    },

    getMockData() {
      return [
        {
          id: 1,
          genId: 'GEN20240101001',
          keywords: '糖尿病饮食管理指南',
          genType: '01',
          genStatus: '2',
          content: '糖尿病是一种常见的慢性疾病，合理的饮食管理对于控制血糖至关重要...',
          createTime: '2024-01-15 10:30:00',
          updateTime: '2024-01-15 10:35:00'
        },
        {
          id: 2,
          genId: 'GEN20240101002',
          keywords: '肿瘤免疫治疗新进展',
          genType: '01',
          genStatus: '2',
          content: '近年来，肿瘤免疫治疗取得了突破性进展，为癌症患者带来了新的希望...',
          createTime: '2024-01-14 14:20:00',
          updateTime: '2024-01-14 14:28:00'
        },
        {
          id: 3,
          genId: 'GEN20240101003',
          keywords: '基础总结与梳理',
          genType: '02',
          genStatus: '2',
          content: '本文对近期发表的关于代谢综合征的研究文献进行了系统性总结...',
          createTime: '2024-01-13 09:15:00',
          updateTime: '2024-01-13 09:45:00'
        },
        {
          id: 4,
          genId: 'GEN20240101004',
          keywords: '心血管疾病预防策略',
          genType: '01',
          genStatus: '1',
          content: '',
          createTime: '2024-01-16 16:00:00',
          updateTime: null
        },
        {
          id: 5,
          genId: 'GEN20240101005',
          keywords: '核心结论解读',
          genType: '02',
          genStatus: '2',
          content: '通过对多项临床研究的综合分析，本研究得出以下核心结论...',
          createTime: '2024-01-12 11:30:00',
          updateTime: '2024-01-12 12:00:00'
        }
      ]
    },

    async loadAIConfig() {
      const projectId = localStorage.getItem('projectId')
      if (!projectId) {
        return
      }
      try {
        const res = await getAIConfig(projectId)
        if (res.code === 200 && res.data) {
          this.aiConfig = {
            science: res.data.science !== false,
            literature: res.data.literature !== false,
            caseAnalysis: res.data.caseAnalysis !== false
          }
        }
      } catch (err) {
        console.error('加载AI配置失败:', err)
      }
    },


    openGenerateModal() {
      this.showGenerateModal = true
    },

    handleGenSuccess(doc) {
      // 生成成功后刷新列表
      this.list.unshift(doc)
      this.showGenerateModal = false
      Message.success('生成成功！')
    },

    handleGenClose() {
      this.showGenerateModal = false
      this.getGenerateList()
    },


    getStatusText(status) {
      const statusMap = {
        'waiting': '排队等待',
        'running': '生成中',
        'succeeded': '成功',
        'failed': '失败',
        '1': '生成中',
        '2': '成功',
        '3': '失败'
      }
      return statusMap[status] || '未知状态'
    },

    getDocTypeLabel(type) {
      const typeMap = {
        '01': '科普征文',
        '02': '文献解读',
        '03': '案例解读'
      }
      return typeMap[type] || '未知类型'
    },

    stripHtmlAndTruncate(html, maxLength) {
      if (!html) return ''
      // 去除 HTML 标签
      const plainText = html.replace(/<[^>]*>/g, '')
      // 去除多余的空白字符
      const cleanedText = plainText.replace(/\s+/g, ' ').trim()
      // 截取指定长度
      if (cleanedText.length <= maxLength) {
        return cleanedText
      }
      return cleanedText.substring(0, maxLength) + '...'
    },

    getStatusTagType(status) {
      const typeMap = {
        'waiting': 'warning',
        'running': 'primary',
        'succeeded': 'success',
        'failed': 'danger',
        '1': 'primary',
        '2': 'success',
        '3': 'danger'
      }
      return typeMap[status] || 'info'
    },

    getStatusClass(status) {
      const classMap = {
        'waiting': 'status-waiting',
        'running': 'status-running',
        'succeeded': 'status-success',
        'failed': 'status-failed',
        '1': 'status-running',
        '2': 'status-success',
        '3': 'status-failed'
      }
      return classMap[status] || ''
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return '—'
      return date.toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-')
    },

    onRefresh() {
      this.getGenerateList()
      Message.success('刷新成功')
    },


  }
}
</script>

<style lang="scss" scoped>
// ─── Token System ───
$base-bg: #F8F8F7;
$surface: #FFFFFF;
$border: #E8E6E3;
$border-light: #F0EEEC;
$text: #1A1A1A;
$text-secondary: #57534E;
$text-muted: #A8A29E;

$teal: #0F766E;
$teal-light: #F0FDFA;
$blue: #1E40AF;
$blue-light: #EFF6FF;
$purple: #7C3AED;
$purple-light: #F5F3FF;
$success: #059669;
$warning: #D97706;
$danger: #DC2626;

$radius: 8px;

// ─── Page Container ───
.submission-list-page {
  background: $base-bg;
  min-height: 100vh;
  padding: 20px 24px;
  padding-bottom: 80px;
}

// ─── Header ───
.page-header {
  margin-bottom: 20px;
  padding: 0 4px;

  .header-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 4px;

    i {
      font-size: 22px;
      color: $teal;
    }

    h2 {
      font-size: 20px;
      font-weight: 700;
      color: $text;
      margin: 0;
      letter-spacing: -0.3px;
    }
  }

  .header-desc {
    font-size: 13px;
    color: $text-muted;
    margin: 0;
    padding-left: 32px;
  }
}

// ─── Filter Bar ───
.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: $surface;
  padding: 4px 8px 4px 4px;
  border-radius: $radius;
  margin-bottom: 16px;
  border: 1px solid $border;
}

.filter-left {
  flex: 1;
}

.filter-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-filter {
  width: 110px;

  ::v-deep .el-input__inner {
    border-radius: 6px;
    border-color: $border;
    font-size: 13px;
    height: 32px;
    line-height: 32px;

    &:focus {
      border-color: $teal;
    }
  }
}

.filter-tabs {
  ::v-deep .el-tabs__header {
    margin: 0;
  }

  ::v-deep .el-tabs__nav-wrap::after {
    display: none;
  }

  ::v-deep .el-tabs__item {
    font-size: 13px;
    font-weight: 500;
    color: $text-muted;
    padding: 0 16px;
    height: 40px;
    line-height: 40px;
    transition: color 0.2s;

    &:hover {
      color: $text-secondary;
    }
  }

  ::v-deep .el-tabs__item.is-active {
    color: $teal;
  }

  ::v-deep .el-tabs__active-bar {
    background: $teal;
    height: 2px;
  }
}

.refresh-btn {
  margin-bottom: 0;
  color: $text-muted;
  font-size: 13px;
  padding: 6px 8px;

  &:hover {
    color: $teal;
  }
}

.add-btn {
  margin-bottom: 0;
  background: $teal;
  border: none;
  border-radius: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.2px;

  &:hover {
    background: #0D6B63;
  }
}

// ─── Task Cards ───
.task-card {
  background: $surface;
  border-radius: $radius;
  padding: 18px 20px;
  margin-bottom: 10px;
  border: 1px solid $border;
  border-left: 3px solid $border;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-left-color: $teal;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }

  &.is-running {
    border-left-color: $warning;
  }

  &.is-failed {
    border-left-color: $danger;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.doc-type-badge {
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;

  &.type-01 {
    background: $teal-light;
    color: $teal;
  }

  &.type-02 {
    background: $blue-light;
    color: $blue;
  }

  &.type-03 {
    background: $purple-light;
    color: $purple;
  }
}

// Status pill
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &.status-success {
    color: $success;
    .status-dot { background: $success; }
  }

  &.status-running {
    color: $warning;
    .status-dot {
      background: $warning;
      animation: pulse-dot 1.5s ease-in-out infinite;
    }
  }

  &.status-failed {
    color: $danger;
    .status-dot { background: $danger; }
  }

  &.status-waiting {
    color: $text-muted;
    .status-dot {
      background: $text-muted;
      animation: pulse-dot 2s ease-in-out infinite;
    }
  }
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.card-body {
  margin-bottom: 14px;
}

.task-title {
  font-size: 15px;
  font-weight: 600;
  color: $text;
  margin: 0 0 6px 0;
  line-height: 1.5;
  letter-spacing: -0.1px;
}

.task-preview {
  font-size: 13px;
  color: $text-muted;
  line-height: 1.65;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid $border-light;
}

.time-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: $text-muted;

  .time-item {
    display: flex;
    align-items: center;
    gap: 3px;

    i {
      font-size: 11px;
    }
  }

  .time-divider {
    color: $border;
  }
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn {
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;

  &.edit-btn {
    color: $text-secondary;

    &:hover {
      color: $teal;
      background: $teal-light;
    }
  }

  &.download-btn {
    color: $text-secondary;

    &:hover {
      color: $blue;
      background: $blue-light;
    }
  }

  &.publish-btn {
    background: $teal;
    border: none;
    color: #fff;
    padding: 5px 14px;
    font-weight: 500;

    &:hover {
      background: #0D6B63;
    }
  }
}

// ─── Load More ───
.load-more {
  text-align: center;
  padding: 20px 0;
}

.finished-text {
  color: $text-muted;
  font-size: 12px;
}

// ─── Editor Dialog ───
.editor-page {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.editor-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid $border;
  background: #FAFAF9;

  .editor-back-btn {
    font-size: 13px;
    color: $text-secondary;
  }

  .editor-word-count {
    font-size: 12px;
    color: $text-muted;
    font-variant-numeric: tabular-nums;
  }

  .editor-save-btn {
    background: $teal;
    border: none;
    font-size: 13px;
    font-weight: 500;

    &:hover {
      background: #0D6B63;
    }
  }
}

// ─── Empty State ───
::v-deep .el-empty {
  padding: 40px 0;

  .el-empty__description p {
    color: $text-muted;
    font-size: 13px;
  }
}

// ─── Preview ───
.preview-wrapper {
  height: 75vh;
  display: flex;
  flex-direction: column;
}

.preview-iframe {
  flex: 1;
  width: 100%;
  border: none;
}

// ─── Generate Dialog Overrides ───
.generate-task-dialog {
  ::v-deep .el-dialog__body {
    padding: 0;
    max-height: 85vh;
    overflow-y: auto;
  }
}

.generate-modal-content {
  padding: 0;
}
</style>
